// askSmart-ui/src/pages/TOSP/components/LeaseReview/leaseSource.ts
import { getMergedSteps } from "../../../../components/SMRTDocsTrafficModal";
import { resolveServiceSteps } from "../../../smrtdocsServices";
import { Tenant } from "../../types";

// Where the signed lease comes from.
//
//   tenant.primaryContactEmail → the SMRT Docs user
//                              → the service named "Tenant"
//                              → the document slot on it
//                              → blobName → /smrtdocs/files/:userId/_
//
// The document is found by its place in the template, not by what anything is
// called. A tenant can rename the file they sign to anything at all, and the
// per-tenant document can be titled for its unit — neither changes the fact
// that it occupies the lease slot on the Tenant template. Matching on names
// made the lookup fail for reasons nobody could see; matching on structure
// doesn't.
//
// The email hop is the weak link: it ties a tenant's lease to one named
// contact, so changing the primary contact changes which lease TOSP can see. A
// tenant-to-user link on the tenant record would fix that, and this is the
// only place that would change.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export interface StoredLease {
  ownerUserId: string;
  blobName: string;
  /**
   * The service the lease was found on.
   *
   * Returned even when nothing was asked, so a tenancy with a single lease
   * still records where it lives — otherwise the question would be settled
   * for multi-lease contacts and left open for everyone else.
   */
  serviceId: string;
  /** Shown above the viewer so the reviewer knows what they have got open. */
  label: string;
}

/** One of several leases a contact holds, offered for the reviewer to pick. */
export interface LeaseServiceChoice {
  serviceId: string;
  serviceName: string;
  /** The document on that service, when it has one. May be empty. */
  documentName: string;
}

export type LeaseLookup =
  | { status: "found"; lease: StoredLease }
  /**
   * The contact holds more than one tenancy and this one hasn't been tied to a
   * service yet. The caller asks which, then saves the answer on the tenant so
   * the question isn't repeated.
   */
  | { status: "choose"; services: LeaseServiceChoice[] }
  | { status: "missing"; reason: string };

/**
 * Whether a service holds tenant paperwork.
 *
 * There used to be exactly one service called "Tenant", shared by every
 * tenant, and this was an equality check against that name. Documents are now
 * stored per service, so a shared service means every tenant's lease sits in
 * one place — the copies are per tenant instead: "Tenant - MJ Byrne's",
 * "Tenant - Chill BM".
 *
 * So the test is the family, not the name: "Tenant" on its own, or "Tenant"
 * followed by a separator. Which of the family belongs to a given tenant is
 * not decided here — that's decided by which one is assigned to them.
 *
 * Exported because the Add Tenant flow starts from the same set: the
 * candidates are whoever the org has assigned to any of these, and the lease
 * is then found on that tenant's own one.
 */
export const isTenantService = (name: unknown): boolean =>
  /^tenant(\s*[-–—:]\s*.+)?$/i.test(String(name ?? "").trim());

/**
 * An assignment record names service INDICES, not names or ids.
 *
 * Two shapes, both still written by the SMRT Docs screen: one service, or
 * several. Anything else — including the legacy bare number — is read as
 * nothing rather than guessed at, since a wrong index would name an unrelated
 * service.
 */
export const assignedIndices = (assignment: any): number[] => {
  if (assignment?.mode === "multi") {
    return Array.isArray(assignment.values) ? assignment.values : [];
  }
  if (assignment?.mode === "single") {
    return typeof assignment.value === "number" ? [assignment.value] : [];
  }
  return [];
};

/**
 * Field types that carry a document: "template" is one the org issues for
 * signature — which is what a lease is — "certificate" is one the tenant
 * uploads, and "" is a document added but not yet typed, which the editor
 * creates by default. "info" is the only one that never holds a file.
 *
 * Listed as what a document ISN'T, because the editor's "+ Add Document"
 * button makes documents regardless of what the type is later set to, and
 * excluding an untyped one hid a real lease.
 */
const NON_DOCUMENT_TYPES = ["info"];

/** Only used to choose between several documents, never to find one. */
const LEASE_NAME = /lease/i;

const getJson = async (path: string): Promise<any> => {
  const res = await fetch(`${BACKEND}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`${path} returned ${res.status}`);
  return res.json();
};

const nameOf = (field: any): string => field?.label || field?.name || field?.key || "";

// Every field on a service, across all its steps — including the documents
// added for this one tenant.
//
// A service's steps live on `service.data.steps`, not `service.steps`. Reading
// the wrong one returns undefined for every service, which is silent: each
// service simply appears to have no fields, so nothing can be found and
// nothing looks broken. `getMergedSteps` is the same helper the SMRT Docs
// screens use, so this can't drift from how they read the same records.
//
// The whole service list is needed, not just the one service: a per-tenant
// "Tenant - X" may be a Sub Service, whose lease slot is defined on the
// "Tenant" Master and merged in by resolveServiceSteps. Reading the service
// alone would find no lease on exactly the services most likely to hold one.
const fieldsOf = (
  services: any[],
  service: any,
  userId: string,
  state: any,
  index: number,
): any[] =>
  getMergedSteps(
    resolveServiceSteps(services, service),
    userId,
    state?.localDocs,
    index,
  ).flatMap((step: any) => step?.fields || []);

/**
 * Which service each document slot belongs to.
 *
 * A tenant contact can be on several services, and their documents all land in
 * one flat `files`/`issued` map keyed by field key alone — so the key itself
 * says nothing about where it came from. Attribution has to be rebuilt from
 * the definitions: a field on `services[i]` belongs to service i, and a local
 * document filed under `byService[i]` belongs to service i.
 *
 * Without this the lookup will happily open a document from another service,
 * which is exactly what it did.
 */
const attributeSlots = (
  services: any[],
  userId: string,
  state: any,
): { owners: Map<string, Set<number>>; fields: Map<string, any> } => {
  const owners = new Map<string, Set<number>>();
  const fields = new Map<string, any>();

  services.forEach((service, index) => {
    // A Master is an abstract template — never assignable, so no user's
    // document can belong to it. Skipping it is not a tidy-up: its slots are
    // inherited by every Sub Service beneath it, and a Master is created before
    // its children, so letting it claim them attributed every tenant's lease to
    // a service nobody can hold.
    if (service?.isMaster) return;

    for (const field of fieldsOf(services, service, userId, state, index)) {
      if (!field?.key) continue;
      if (!fields.has(field.key)) fields.set(field.key, field);
      // A SET, because a key genuinely belongs to more than one service now.
      // Sub Services under one Master all resolve the same inherited keys, and
      // "first one wins" silently discarded every sibling's documents — the
      // lease included, which read as no lease existing at all.
      const held = owners.get(field.key) ?? new Set<number>();
      held.add(index);
      owners.set(field.key, held);
    }
  });

  return { owners, fields };
};

interface DocumentRef {
  blobName: string;
  /** The stored PDF's own name — the only label some documents have. */
  fileName?: string;
}

// The PDF behind a document slot — the ACTIVE (executed) document only.
//
// A document lives in two places over its life: the unsigned original on
// `issued[key].originalFile` while it is out for signature, and the executed
// copy on `files[key]` once signing completes. TOSP is confirming lease terms,
// so it connects to the executed copy alone — the one SMRT Docs treats as
// Active. The unsigned original is deliberately NOT a fallback: a draft out for
// signature isn't the lease yet. And because an amendment (SMRT Docs "R")
// archives the superseded copy and lands the new one in `files[key]` under the
// same slot, reading `files[key]` always follows through to whatever is
// currently Active.
const documentFor = (state: any, key: string): DocumentRef | null => {
  const executed = (state?.files || {})[key];
  if (executed?.blobName) {
    return { blobName: executed.blobName, fileName: executed.name };
  }
  return null;
};

// The active document carries no "unsigned copy" caveat — it's the executed one
// by definition — so the label is just what the slot is called.
const labelFor = (field: any, doc: DocumentRef, key: string): string =>
  field?.label || field?.name || doc.fileName || key;

/** Whole-token match, so unit "C1" doesn't select a document for "C10". */
const mentionsUnit = (name: string, suite: string): boolean => {
  const escaped = suite.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (!escaped) return false;
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i").test(name || "");
};

export async function resolveStoredLease(
  tenant: Tenant,
  /** Picks this occupancy's lease when a tenant holds several. */
  suiteNumber?: string,
  /** A document already chosen for this occupancy — settled, so not re-asked. */
  rememberedKey?: string,
): Promise<LeaseLookup> {
  const email = (tenant.primaryContactEmail || "").trim().toLowerCase();
  if (!email) {
    return { status: "missing", reason: "This tenant has no primary contact email." };
  }

  try {
    const [userData, serviceData, assignmentData] = await Promise.all([
      getJson("/approved_users"),
      getJson("/smrtdocs/services"),
      getJson("/smrtdocs/assignments"),
    ]);

    const users: any[] = userData.users || userData || [];
    const user = users.find(
      (candidate) => (candidate.email || "").trim().toLowerCase() === email,
    );
    if (!user) {
      return {
        status: "missing",
        reason: `No SMRT Docs user with the address ${tenant.primaryContactEmail}.`,
      };
    }
    const userId: string = user.user_id || user.id;

    const services: any[] = serviceData.services || [];
    const assignments: Record<string, any> = assignmentData.assignments || {};

    // This tenancy's own service.
    //
    // The tenant record names it once it has been reviewed — which is the only
    // thing that can answer the question when one contact holds two tenancies,
    // since the contact and the naming convention are identical for both.
    // Until then, fall back to the tenant services they're assigned.
    const chosenId = (tenant.smrtDocsServiceId || "").trim();
    const chosenIndex = chosenId
      ? services.findIndex((service) => service?.id === chosenId)
      : -1;

    if (chosenId && chosenIndex < 0) {
      // The tenancy names a service that is no longer there — deleted, or the
      // record predates its id. Said plainly rather than quietly falling back
      // to a different lease.
      return {
        status: "missing",
        reason:
          `This tenancy's lease is on a SMRT Docs service that no longer exists ` +
          `(${chosenId}). Re-review the lease to point it at the right one.`,
      };
    }

    const theirs =
      chosenIndex >= 0
        ? [chosenIndex]
        : assignedIndices(assignments[userId]).filter((index) =>
            isTenantService(services[index]?.name),
          );

    if (theirs.length === 0) {
      // Masters excluded: this text tells an admin what they could assign, and
      // a Master is not assignable. Naming one sends them to a dead end.
      const family = services
        .map((service, index) => ({ service, index }))
        .filter(({ service }) => isTenantService(service?.name) && !service?.isMaster);
      return {
        status: "missing",
        reason: family.length
          ? `${user.name || tenant.primaryContactEmail} isn't assigned a tenant service. ` +
            `The org has: ${family.map((f) => `“${f.service.name}”`).join(", ")}.`
          : `No tenant service exists. Found: ${
              services.map((service) => `“${service?.name}”`).join(", ") || "none"
            }.`,
      };
    }

    if (theirs.length > 1) {
      // One contact, two tenancies — two leases, and nothing in the data says
      // which is this one's. Asked rather than guessed, and the answer is kept
      // on the tenant record so it's asked exactly once.
      //
      // Each option carries the document it holds, read from that service's
      // own slice. It costs one request per candidate and it's the difference
      // between choosing a lease and choosing a service name.
      const choices: LeaseServiceChoice[] = [];
      for (const index of theirs) {
        const service = services[index];
        const serviceId = String(service?.id || "");
        let documentName = "";
        if (serviceId) {
          try {
            const slice = await getJson(
              `/smrtdocs/user-state/${encodeURIComponent(userId)}?serviceId=${encodeURIComponent(serviceId)}`,
            );
            const held = fieldsOf(services, service, userId, slice, index)
              .filter((field: any) => !NON_DOCUMENT_TYPES.includes(field?.type))
              .map((field: any) => ({ field, doc: documentFor(slice, field.key) }))
              .filter((entry) => Boolean(entry.doc));
            const best =
              held.find((entry) => LEASE_NAME.test(nameOf(entry.field))) || held[0];
            documentName = best ? nameOf(best.field) || best.doc?.fileName || "" : "";
          } catch {
            // A slice that won't load shouldn't cost the admin the choice —
            // the option is still offered, just without its document name.
          }
        }
        choices.push({
          serviceId,
          serviceName: String(service?.name || "(unnamed service)"),
          documentName,
        });
      }
      return { status: "choose", services: choices };
    }

    const templateIndex = theirs[0];
    const templateId = services[templateIndex]?.id;

    // Only this service's documents come back. Asking without a service id
    // returns every service merged, which is how one tenant's lease used to
    // surface under another's.
    const state = await getJson(
      `/smrtdocs/user-state/${encodeURIComponent(userId)}` +
        (templateId ? `?serviceId=${encodeURIComponent(templateId)}` : ""),
    );

    // Every document slot: the service's own, plus any added for this tenant
    // alone. A shared service can legitimately have none of its own — a lease
    // is per-tenant by nature, so it's attached to the tenant, not the
    // template every tenant shares.
    const { owners, fields } = attributeSlots(services, userId, state);

    // A document settled on before goes straight through — the whole point of
    // remembering it. Falls through to the usual search if it's since been
    // removed, rather than dead-ending on a stale key.
    if (rememberedKey) {
      const doc = documentFor(state, rememberedKey);
      if (doc) {
        const field = fields.get(rememberedKey);
        return {
          status: "found",
          lease: {
            ownerUserId: userId,
            blobName: doc.blobName,
            serviceId: String(templateId || ""),
            label: labelFor(field, doc, rememberedKey),
          },
        };
      }
    }

    // Only this service's documents. A slot belonging solely to other services
    // is excluded; one that can't be attributed at all is allowed through,
    // since the legacy shape records no service and refusing it would hide a
    // real lease.
    //
    // Membership, not equality: an inherited slot belongs to this Sub Service
    // AND to each of its siblings, and all of them are equally entitled to it.
    const slots = new Map<string, any>();
    for (const [key, field] of fields) {
      if (NON_DOCUMENT_TYPES.includes(field.type)) continue;
      const holders = owners.get(key);
      if (holders && !holders.has(templateIndex)) continue;
      slots.set(key, field);
    }

    const candidates = [...slots.values()]
      .map((field) => ({ field, doc: documentFor(state, field.key) }))
      .filter((entry): entry is { field: any; doc: DocumentRef } => Boolean(entry.doc));

    if (candidates.length === 0) {
      // A lease may well exist but be out for signature. Now that the unsigned
      // original is no longer a fallback, that case would otherwise read as "no
      // lease at all" — so name it: an amendment mid-signing is a different
      // situation from a tenant that has never had a lease on file.
      const awaiting = [...slots.keys()].some(
        (key) => (state?.issued || {})[key]?.originalFile?.blobName,
      );
      if (awaiting) {
        return {
          status: "missing",
          reason:
            "The lease on the Tenant service is out for signature and not yet " +
            "active. TOSP reviews the signed (Active) document — try again once " +
            "it has been signed.",
        };
      }

      // A document's link to a service lives only in its field definition —
      // the upload records the field key and nothing else — so if no
      // definition can be found on the Tenant service, no amount of searching
      // storage will say which document belongs to it. Report the structure
      // seen rather than guessing at one.
      const localKeys = Object.keys(state?.localDocs?.byService || {});
      const held = new Set([
        ...Object.keys(state.files || {}),
        ...Object.keys(state.issued || {}),
      ]);
      return {
        status: "missing",
        reason:
          `No document is defined on the Tenant service (index ${templateIndex}). ` +
          `Its steps: ${(services[templateIndex]?.steps || []).length}. ` +
          `Local document slices: ${localKeys.length ? localKeys.join(", ") : "none"}. ` +
          `${user.name || tenant.primaryContactEmail} holds ${held.size} document(s) in total.`,
      };
    }

    // One document is the whole answer — the ordinary case, and the reason
    // nothing here depends on what it's called.
    //
    // Where there are several, the unit decides first: a tenant's shop and
    // patio leases are both leases, so the word alone would pick one at
    // random. Only then does the name break the tie, and only among documents
    // that are still out for signature or executed — a slot with nothing in it
    // never wins.
    const named = candidates.filter((entry) => entry.field?.label || entry.field?.name);
    const pool = named.length > 0 ? named : candidates;

    const chosen =
      pool.length === 1
        ? pool[0]
        : (suiteNumber &&
            pool.find((entry) => mentionsUnit(nameOf(entry.field), suiteNumber))) ||
          pool.find((entry) => LEASE_NAME.test(nameOf(entry.field))) ||
          null;

    // Several leases on the one service and nothing to separate them by.
    // Guessing would put the wrong unit's terms in front of the reviewer.
    if (!chosen) {
      return {
        status: "missing",
        reason: `The Tenant service has ${candidates.length} documents and none names unit ${
          suiteNumber || "this occupancy"
        }: ${candidates.map((entry) => `“${nameOf(entry.field)}”`).join(", ")}.`,
      };
    }

    return {
      status: "found",
      lease: {
        ownerUserId: userId,
        blobName: chosen.doc.blobName,
        serviceId: String(templateId || ""),
        label: nameOf(chosen.field) || chosen.doc.fileName || "",
      },
    };
  } catch (err) {
    console.error("[TOSP] lease lookup failed:", err);
    return {
      status: "missing",
      reason: `Couldn't reach SMRT Docs: ${
        err instanceof Error ? err.message : "unknown error"
      }`,
    };
  }
}

/** Pulls a stored lease down as bytes for pdf.js. */
export async function fetchStoredLease(lease: StoredLease): Promise<Uint8Array> {
  const res = await fetch(
    `${BACKEND}/smrtdocs/files/${encodeURIComponent(lease.ownerUserId)}/_?blobName=${encodeURIComponent(
      lease.blobName,
    )}&format=base64`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`Couldn't fetch the lease (${res.status}).`);

  const { base64 } = await res.json();
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
